"use client";

import DataTable, { type Column } from "@/components/Datatable";
import Header from "@/components/layouts/Header";
import { Button } from "@/components/ui/button";
import { DeleteDialog } from "@/components/ui/delete-dialog";
import { useDownloads } from "@/contexts/DownloadsContext";
import { errorHandler, formatDateTime } from "@/lib/utils";
import {
  trunkReportService,
  type BackendFile,
} from "@/services/api/trunkReport";
import { CheckCircle, Clock, Download, FileText } from "lucide-react";
import { useState } from "react";

const Downloads = () => {
  const { deleteBackendFile } = useDownloads();
  const [reloadTableKey, setReloadTableKey] = useState(0);

  const fetchData = trunkReportService.downloadIndex;

  const handleDelete = async (id: string | number) => {
    try {
      const success = await deleteBackendFile(id as number);
      if (success) {
        setReloadTableKey((prev) => prev + 1);
      }
      return success;
    } catch (err) {
      errorHandler(err, "Erro ao excluir download");
      return false;
    }
  };

  const handleDownload = async (file: BackendFile) => {
    if (file.status === 1) {
      try {
        const blob = await trunkReportService.downloadFile(file.id);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = file.original_name || file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        errorHandler(error, "Erro ao baixar arquivo");
      }
    }
  };

  const getStatusIcon = (status?: number) => {
    switch (status) {
      case 0:
        return <Clock size={16} className="text-yellow-500" />;
      case 1:
        return <CheckCircle size={16} className="text-green-500" />;
      default:
        return <FileText size={16} className="text-gray-500" />;
    }
  };

  const getStatusText = (status?: number): string => {
    switch (status) {
      case 0:
        return "Processando";
      case 1:
        return "Concluído";
      default:
        return "Pendente";
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (
      Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
    );
  };

  const columns: Column<BackendFile>[] = [
    {
      key: "original_name",
      label: "Nome do Arquivo",
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          <FileText size={16} className="text-gray-500" />
          <div>
            <div className="font-medium">{row.original_name || row.name}</div>
            <div className="text-xs text-gray-500">
              {formatFileSize(row.size)}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      filterable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          {getStatusIcon(row.status)}
          <span>{getStatusText(row.status)}</span>
        </div>
      ),
      filter: {
        options: [
          { key: "0", value: "Processando" },
          { key: "1", value: "Concluído" },
        ],
        type: "select",
      },
    },
    {
      key: "content_type",
      label: "Tipo",
      render: (row) => {
        const type = row.content_type.split("/")[1]?.toUpperCase() || "ARQUIVO";
        return (
          <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs">
            {type}
          </span>
        );
      },
    },
    {
      key: "created_at",
      label: "Data de Criação",
      sortable: true,
      advancedFilter: { type: "date-range" },
      render: (row) => formatDateTime(row.created_at),
    },
    {
      key: "actions",
      label: "",
      render: (row, { onDelete }) => (
        <div className="flex space-x-2 justify-end">
          {row.status === 1 && (
            <Button
              size="sm"
              className="dark:bg-primary-600 bg-primary-600 text-white hover:bg-primary-700"
              onClick={() => handleDownload(row)}
            >
              <Download className="h-4 w-4" />
            </Button>
          )}
          <DeleteDialog
            onDelete={onDelete}
            row={row}
            message="Tem certeza que deseja excluir este arquivo?"
          />
        </div>
      ),
    },
  ];

  return (
    <>
      <Header title="Downloads" />
      <DataTable
        key={reloadTableKey}
        fetchData={fetchData}
        columns={columns}
        onDelete={handleDelete}
        searchPlaceholder="Buscar por nome do arquivo, tipo ou tamanho..."
      />
    </>
  );
};

export default Downloads;
